import { useState } from "react";
import { toast } from "sonner";
import { Send, Mail, Phone, MapPin, Clock } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { images } from "../data/images";
import Reveal from "../components/Reveal";

const initialState = { name: "", email: "", subject: "", message: "" };

// >>> Cambia este correo por el tuyo
const CONTACT_EMAIL = "hello@sodeco.org";

const Contact = () => {
    const { t } = useLanguage();
    const [form, setForm] = useState(initialState);

    const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        const subject = encodeURIComponent(`[SODECO] ${form.subject}`);
        const body = encodeURIComponent(
            `${form.message}\n\n— ${form.name}\n${form.email}`
        );
        // Open user's email client with the message pre-filled
        window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
        toast.success(t.contact.sent);
        setForm(initialState);
    };

    return (
        <main data-testid="contact-page" className="bg-white">
            <section className="relative pt-32 pb-16 md:pt-44 md:pb-24 overflow-hidden border-b border-[var(--sodeco-border)]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <span className="text-[11px] uppercase tracking-[0.4em] text-[var(--sodeco-muted)]">
                                {t.contact.eyebrow}
                            </span>
                            <div className="mt-3">
                                <span className="yellow-rule" />
                            </div>
                        </Reveal>
                        <Reveal delay={100}>
                            <h1
                                data-testid="contact-title"
                                className="mt-6 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
                            >
                                {t.contact.title}
                            </h1>
                        </Reveal>
                        <Reveal delay={200}>
                            <p className="mt-6 max-w-xl text-[var(--sodeco-muted)] leading-relaxed">
                                {t.contact.subtitle}
                            </p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-5">
                        <Reveal delay={150}>
                            <img
                                src={images.contactHeader}
                                alt=""
                                className="w-full h-[280px] md:h-[380px] object-cover"
                                loading="lazy"
                            />
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-16">
                <div className="lg:col-span-7">
                    <form
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="space-y-8"
                    >
                        <div className="grid sm:grid-cols-2 gap-8">
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)] mb-3">
                                    {t.contact.name}
                                </label>
                                <input
                                    data-testid="contact-name-input"
                                    name="name"
                                    required
                                    value={form.name}
                                    onChange={onChange}
                                    className="w-full bg-transparent border-b-2 border-[var(--sodeco-border)] py-3 text-lg font-serif focus:border-[var(--sodeco-yellow-deep)] transition-colors"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)] mb-3">
                                    {t.contact.email}
                                </label>
                                <input
                                    data-testid="contact-email-input"
                                    name="email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={onChange}
                                    className="w-full bg-transparent border-b-2 border-[var(--sodeco-border)] py-3 text-lg font-serif focus:border-[var(--sodeco-yellow-deep)] transition-colors"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)] mb-3">
                                {t.contact.subject}
                            </label>
                            <input
                                data-testid="contact-subject-input"
                                name="subject"
                                required
                                value={form.subject}
                                onChange={onChange}
                                className="w-full bg-transparent border-b-2 border-[var(--sodeco-border)] py-3 text-lg font-serif focus:border-[var(--sodeco-yellow-deep)] transition-colors"
                            />
                        </div>

                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)] mb-3">
                                {t.contact.message}
                            </label>
                            <textarea
                                data-testid="contact-message-input"
                                name="message"
                                required
                                rows={6}
                                value={form.message}
                                onChange={onChange}
                                className="w-full bg-transparent border-b-2 border-[var(--sodeco-border)] py-3 text-lg font-serif focus:border-[var(--sodeco-yellow-deep)] transition-colors resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            data-testid="contact-submit-btn"
                            className="btn-pill"
                        >
                            <Send size={16} />
                            {t.actions.send}
                        </button>
                    </form>
                </div>

                <aside className="lg:col-span-5 space-y-10">
                    <div>
                        <h3 className="text-[11px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)] mb-5 flex items-center gap-2">
                            <MapPin size={12} /> {t.contact.officeTitle}
                        </h3>
                        <ul className="space-y-5">
                            {t.contact.offices.map((o, i) => (
                                <li
                                    key={i}
                                    className="border-l-2 pl-5"
                                    style={{ borderColor: "var(--sodeco-yellow)" }}
                                >
                                    <div className="font-serif text-xl">{o.city}</div>
                                    <div className="text-sm text-[var(--sodeco-muted)] mt-1">{o.line}</div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="grid grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-[11px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)] mb-3 flex items-center gap-2">
                                <Clock size={12} /> {t.contact.hoursTitle}
                            </h3>
                            <p className="font-serif text-lg">{t.contact.hoursLine}</p>
                        </div>
                        <div>
                            <h3 className="text-[11px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)] mb-3 flex items-center gap-2">
                                <Mail size={12} /> {t.contact.emailTitle}
                            </h3>
                            <p className="font-serif text-lg">{CONTACT_EMAIL}</p>
                        </div>
                        <div>
                            <h3 className="text-[11px] uppercase tracking-[0.25em] text-[var(--sodeco-muted)] mb-3 flex items-center gap-2">
                                <Phone size={12} /> {t.contact.phoneTitle}
                            </h3>
                            <p className="font-serif text-lg">+1 (809) 865-5958</p>
                        </div>
                    </div>
                </aside>
            </section>
        </main>
    );
};

export default Contact;
